// deps
import React, { ReactElement, memo, useCallback, useContext, useRef } from "react";

// internal deps
import { DeliverySpeedCrouton, DeliverySpeedOption } from "@swiggy-private/cart-ui";
import { AnalyticsContext, AnalyticsContextValue } from "@swiggy-private/checkout-core";
import { ElementObserver } from "@swiggy-private/core-ui";

// analytics
import {
    impressionCartDeliverySpeed,
    impressionOptInWidget,
    impressionExpressBuyBlckNudge,
    clickExpressBuyBlckNudge,
    impressionDeliveryTypeOffer,
    impressionDeliveryTypeOfferExpired,
} from "@cart-client/analytics/events";

// contexts
import { CartContext } from "@cart-client/contexts/CartContext";

// hooks
import { useCartDeliverySpeed } from "@cart-client/hooks/useCartDeliverySpeed";
import { useCartRendererAnalytics } from "@cart-client/hooks/useCartRenderedAnalytics";

// helpers
import { isEmpty } from "@swiggy-private/js-utils";
import { DELIVERY_SPEED_WIDGET_TEST_IDS } from "@swiggy-private/cart-test-ids";
import { DeliverySpeedDetail, EcoSaverLottieSuccess } from "@swiggy-private/cart-types";
import { UpdateCartContext } from "@cart-client/contexts/UpdateCartContext";

export interface CartDeliverySpeedProps {
    isDeliverySpeedCroutonActive: boolean;
    setDeliverySpeedCroutonActive: (isDeliverySpeedCroutonActive: boolean) => void;
    canShowDeliverySpeedCrouton: boolean;
    setCroutonVisibility: (croutonVisibility: boolean) => void;
    cartTopOffset: number;
    ecoSaverLottieSuccess?: EcoSaverLottieSuccess | null;
}

function CartDeliverySpeed(props: CartDeliverySpeedProps): ReactElement | null {
    const {
        cartState: { localCart, reviewedCart: cartResponse },
    } = useContext(CartContext);
    const { impressionEvent } = useContext(AnalyticsContext);
    const { selectedAddress, cart_id: cartId, restaurantId, cart_category_type: cartCategoryType } = localCart || {};
    const { delivery_type_opt_in: deliveryTypeOptIn, sla_options: slaOptions } = selectedAddress || [];
    const {
        onDeliverySpeedItemClick,
        hideDeliverySpeedCrouton,
        selectedSlaOption,
        deliverySpeedCroutonText,
        deliverySpeedCroutonSubText,
        deliverySpeedCroutonIcon,
        deliverySpeedWidgetInfoV2,
        onCroutonClick,
        onCroutonLoad,
        onOptInWidgetClick,
        onOfferTimerEnds,
    } = useCartDeliverySpeed({ ...props, cartResponse });
    const { onImpressionAddressPSLABanner } = useCartRendererAnalytics(cartResponse);
    const hasSentCartDeliverySpeedImpressionEvent = useRef(false);
    const hasSentOptInWidgetImpressionEvent = useRef(false);
    const { setShowM2ChalfcardOnClick } = useContext(UpdateCartContext);

    const onViewEnter = useCallback(() => {
        if (!hasSentCartDeliverySpeedImpressionEvent.current) {
            const impressionSlaOptions = slaOptions?.map((slaOption: DeliverySpeedDetail) => ({
                type: slaOption.type,
                strategy: slaOption.strategy,
                slaRange: `${slaOption.sla_range_min}-${slaOption.sla_range_max}`,
                unavailabilityReason: slaOption.unavailabilityReason,
            }));

            impressionCartDeliverySpeed({
                impressionEvent,
                cartId,
                addressId: selectedAddress?.id,
                restaurantId,
                selected: selectedSlaOption?.type,
                source: cartCategoryType,
                slaOptions: impressionSlaOptions,
            });
            hasSentCartDeliverySpeedImpressionEvent.current = true;
        }

        if (props.isDeliverySpeedCroutonActive) {
            hideDeliverySpeedCrouton();
        }
    }, [
        props.isDeliverySpeedCroutonActive,
        impressionEvent,
        cartId,
        selectedAddress?.id,
        restaurantId,
        selectedSlaOption?.type,
        cartCategoryType,
        slaOptions,
        hideDeliverySpeedCrouton,
    ]);

    const onOptInWidgetViewEnter = useCallback(() => {
        if (!hasSentOptInWidgetImpressionEvent.current) {
            impressionOptInWidget(impressionEvent, cartId, selectedAddress?.id);
            hasSentOptInWidgetImpressionEvent.current = true;
        }
    }, [cartId, selectedAddress, impressionEvent]);

    const onSlaCommsViewEnter = useCallback(
        (isVisible: boolean) => {
            if (isVisible) {
                onImpressionAddressPSLABanner(deliverySpeedWidgetInfoV2?.slaComms?.slaText);
            }
        },
        [deliverySpeedWidgetInfoV2?.slaComms?.slaText, onImpressionAddressPSLABanner],
    );

    const onChange = useCallback(
        (option) => {
            onDeliverySpeedItemClick(option.slaData);
        },
        [onDeliverySpeedItemClick],
    );

    const handleImpressionDeliveryTypeOffer = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"], offerMessage?: string, offerExpiry?: number) => {
            return impressionDeliveryTypeOffer(
                cartId,
                selectedAddress?.id,
                restaurantId,
                selectedSlaOption?.type,
            )(impressionEvent, offerMessage, offerExpiry);
        },
        [cartId, restaurantId, selectedAddress?.id, selectedSlaOption?.type],
    );

    const handleImpressionDeliveryTypeOfferExpired = useCallback(
        (impressionEvent: AnalyticsContextValue["impressionEvent"]) => {
            return impressionDeliveryTypeOfferExpired(
                cartId,
                selectedAddress?.id,
                restaurantId,
                selectedSlaOption?.type,
            )(impressionEvent);
        },
        [cartId, restaurantId, selectedAddress?.id, selectedSlaOption?.type],
    );

    if (deliverySpeedWidgetInfoV2 === null) {
        return null;
    }

    return (
        <div data-testid={DELIVERY_SPEED_WIDGET_TEST_IDS.DELIVERY_SPEED_WIDGET_ID}>
            <ElementObserver onViewEnter={onViewEnter}>
                <DeliverySpeedOption
                    options={deliverySpeedWidgetInfoV2?.options}
                    onChange={onChange}
                    optInText={deliverySpeedWidgetInfoV2?.optInText}
                    selectedSlaOption={selectedSlaOption?.type}
                    deliveryTypeOptIn={deliveryTypeOptIn}
                    onOptInWidgetClick={onOptInWidgetClick}
                    onOptInWidgetViewEnter={onOptInWidgetViewEnter}
                    slaComms={deliverySpeedWidgetInfoV2?.slaComms}
                    onSlaCommsViewEnter={onSlaCommsViewEnter}
                    setShowM2ChalfcardOnClick={setShowM2ChalfcardOnClick}
                    impressionExpressBuyBlckNudge={impressionExpressBuyBlckNudge}
                    clickExpressBuyBlckNudge={clickExpressBuyBlckNudge}
                    onOfferTimerEnds={onOfferTimerEnds}
                    impressionDeliveryTypeOffer={handleImpressionDeliveryTypeOffer}
                    impressionDeliveryTypeOfferExpired={handleImpressionDeliveryTypeOfferExpired}
                    ecoSaverLottieSuccess={props.ecoSaverLottieSuccess}
                />
            </ElementObserver>
            {props.isDeliverySpeedCroutonActive && !isEmpty(deliverySpeedCroutonText) && (
                <DeliverySpeedCrouton
                    croutonText={deliverySpeedCroutonText}
                    croutonSubtext={deliverySpeedCroutonSubText}
                    croutonIcon={deliverySpeedCroutonIcon}
                    onClick={onCroutonClick}
                    setCroutonVisibility={props.setCroutonVisibility}
                    onCroutonLoad={onCroutonLoad}
                />
            )}
        </div>
    );
}

export default memo(CartDeliverySpeed);
